import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from './AuthContext'

const LandingPagesContext = createContext(null)

const DEFAULT_FORM_FIELDS = {
  zoom_registration: [
    { id: 'full_name', label: 'Full Name', type: 'text', required: true, enabled: true },
    { id: 'email', label: 'Email', type: 'email', required: true, enabled: true },
    { id: 'phone', label: 'Phone', type: 'tel', required: false, enabled: true }
  ],
  recruiting: [
    { id: 'full_name', label: 'Full Name', type: 'text', required: true, enabled: true },
    { id: 'email', label: 'Email', type: 'email', required: true, enabled: true },
    { id: 'phone', label: 'Phone', type: 'tel', required: true, enabled: true },
    { id: 'current_occupation', label: 'Current Occupation', type: 'text', required: false, enabled: true },
    { id: 'why_interested', label: 'Why are you interested?', type: 'textarea', required: false, enabled: true }
  ],
  client_focused: [
    { id: 'full_name', label: 'Full Name', type: 'text', required: true, enabled: true },
    { id: 'email', label: 'Email', type: 'email', required: true, enabled: true },
    { id: 'phone', label: 'Phone', type: 'tel', required: true, enabled: true },
    {
      id: 'financial_goal',
      label: 'Primary Financial Goal',
      type: 'select',
      options: [
        'Retirement Planning',
        'Income Protection',
        'Debt Reduction',
        'Legacy Planning',
        'College Savings'
      ],
      required: false,
      enabled: true
    }
  ],
  hybrid: [
    { id: 'full_name', label: 'Full Name', type: 'text', required: true, enabled: true },
    { id: 'email', label: 'Email', type: 'email', required: true, enabled: true },
    { id: 'phone', label: 'Phone', type: 'tel', required: true, enabled: true }
  ]
}

const CTA_DEFAULTS = {
  zoom_registration: 'Register Now',
  recruiting: 'Join the Team',
  client_focused: 'Book a Consultation',
  hybrid: 'Take the Next Step'
}

export const LandingPagesProvider = ({ children }) => {
  const { user, professionalProfile } = useAuth()
  const [landingPages, setLandingPages] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [checkingSlug, setCheckingSlug] = useState(false)

  const fetchLandingPages = useCallback(async () => {
    if (!user) {
      setLandingPages([])
      return []
    }

    setLoading(true)
    try {
      const { data, error: fetchError } = await supabase
        .from('landing_pages')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })

      if (fetchError) throw fetchError
      setLandingPages(data ?? [])
      setError(null)
      return data ?? []
    } catch (err) {
      console.error('Error fetching landing pages', err)
      setError(err)
      return []
    } finally {
      setLoading(false)
    }
  }, [user])

  const resetPageCache = useCallback(() => {
    if (!user) return
    fetchLandingPages()
  }, [fetchLandingPages, user])

  useEffect(() => {
    if (user) {
      fetchLandingPages()
    } else {
      setLandingPages([])
    }
  }, [fetchLandingPages, user])

  const uploadImage = useCallback(async (file, pathPrefix = '') => {
    if (!file) return null
    if (!user) throw new Error('You must be logged in to upload images.')

    const fileExt = file.name.split('.').pop()
    const fileName = `${pathPrefix}${Date.now()}-${Math.random().toString(36).slice(2)}.${fileExt}`
    const filePath = `${user.id}/${fileName}`

    const { error: uploadError } = await supabase.storage
      .from('landing-pages')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: true
      })

    if (uploadError) {
      console.error('Image upload failed', uploadError)
      throw uploadError
    }

    const { data: publicUrlData } = supabase.storage
      .from('landing-pages')
      .getPublicUrl(filePath)

    return publicUrlData?.publicUrl ?? null
  }, [user])

  const createLandingPage = useCallback(
    async (payload) => {
      if (!user) throw new Error('You must be logged in to create landing pages.')

      const now = new Date().toISOString()
      const preparedPayload = {
        ...payload,
        user_id: user.id,
        metadata: {
          ...(payload.metadata || {}),
          owner_email: professionalProfile?.email || user.email,
          username: professionalProfile?.username || professionalProfile?.slug || payload.username || null
        },
        created_at: now,
        updated_at: now
      }

      const { data, error: insertError } = await supabase
        .from('landing_pages')
        .insert(preparedPayload)
        .select()
        .single()

      if (insertError) throw insertError

      setLandingPages((prev) => [data, ...prev])
      return data
    },
    [professionalProfile?.email, user]
  )

  const updateLandingPage = useCallback(async (id, payload) => {
    if (!user) throw new Error('You must be logged in to update landing pages.')

    const now = new Date().toISOString()
    const { data, error: updateError } = await supabase
      .from('landing_pages')
      .update({
        ...payload,
        updated_at: now
      })
      .eq('id', id)
      .eq('user_id', user.id)
      .select()
      .single()

    if (updateError) throw updateError

    setLandingPages((prev) => prev.map((page) => (page.id === id ? data : page)))
    return data
  }, [user])

  const deleteLandingPage = useCallback(async (id) => {
    if (!user) throw new Error('You must be logged in to delete landing pages.')

    const { error: deleteError } = await supabase
      .from('landing_pages')
      .delete()
      .eq('id', id)
      .eq('user_id', user.id)

    if (deleteError) throw deleteError

    setLandingPages((prev) => prev.filter((page) => page.id !== id))
  }, [user])

  const toggleActiveState = useCallback(async (id, isActive) => {
    return updateLandingPage(id, { is_active: isActive })
  }, [updateLandingPage])

  const checkSlugAvailability = useCallback(
    async (slug, currentId = null) => {
      if (!slug) return false
      setCheckingSlug(true)
      try {
        const { data, error: slugError } = await supabase
          .from('landing_pages')
          .select('id')
          .eq('slug', slug)
          .maybeSingle()

        if (slugError && slugError.code !== 'PGRST116') {
          throw slugError
        }

        if (!data) return true
        if (currentId && data.id === currentId) return true
        return false
      } catch (err) {
        console.error('Error checking slug', err)
        return false
      } finally {
        setCheckingSlug(false)
      }
    },
    []
  )

  const getLandingPageById = useCallback(
    async (id) => {
      const existing = landingPages.find((page) => page.id === id)
      if (existing) return existing

      const { data, error: fetchError } = await supabase
        .from('landing_pages')
        .select('*')
        .eq('id', id)
        .maybeSingle()

      if (fetchError) throw fetchError
      if (data && data.user_id === user?.id) {
        setLandingPages((prev) => {
          const already = prev.some((page) => page.id === data.id)
          if (already) return prev.map((page) => (page.id === data.id ? data : page))
          return [...prev, data]
        })
      }
      return data
    },
    [landingPages, user?.id]
  )

  const getPublicLandingPage = useCallback(async (username, slug) => {
    if (!username || !slug) return null

    const { data: userRecord, error: userError } = await supabase
      .from('users_pf')
      .select('id, auth_id, username, email, first_name, last_name, bio')
      .eq('username', username)
      .maybeSingle()

    if (userError || !userRecord) {
      if (userError && userError.code !== 'PGRST116') {
        console.error('Error fetching user by username', userError)
      }
      return null
    }

    const { data: page, error: pageError } = await supabase
      .from('landing_pages')
      .select('*')
      .eq('user_id', userRecord.auth_id || userRecord.id)
      .eq('slug', slug)
      .eq('is_active', true)
      .maybeSingle()

    if (pageError) {
      if (pageError.code !== 'PGRST116') {
        console.error('Error fetching landing page', pageError)
      }
      return null
    }

    if (!page) return null

    return { page, owner: userRecord }
  }, [])

  const incrementViewCount = useCallback(async (landingPageId) => {
    if (!landingPageId) return

    await supabase.rpc('increment_landing_page_views', { page_id: landingPageId }).catch(async (rpcError) => {
      console.warn('increment_landing_page_views RPC unavailable, falling back', rpcError)
      const { data: pageData, error: fetchError } = await supabase
        .from('landing_pages')
        .select('view_count')
        .eq('id', landingPageId)
        .single()

      if (fetchError) {
        console.error('Fallback fetch for views failed', fetchError)
        return
      }

      const { error: updateError } = await supabase
        .from('landing_pages')
        .update({ view_count: (pageData?.view_count ?? 0) + 1 })
        .eq('id', landingPageId)

      if (updateError) {
        console.error('Fallback view increment failed', updateError)
      }
    })

    setLandingPages((prev) =>
      prev.map((page) =>
        page.id === landingPageId
          ? {
              ...page,
              view_count: (page.view_count ?? 0) + 1
            }
          : page
      )
    )
  }, [])

  const recordSubmission = useCallback(async ({ landingPageId, formData, ipAddress, userAgent, referrer }) => {
    const { data, error: insertError } = await supabase
      .from('landing_page_submissions')
      .insert({
        landing_page_id: landingPageId,
        form_data: formData,
        ip_address: ipAddress ?? null,
        user_agent: userAgent ?? null,
        referrer: referrer ?? null
      })
      .select()
      .single()

    if (insertError) throw insertError

    const { data: pageData, error: pageError } = await supabase
      .from('landing_pages')
      .select('submission_count')
      .eq('id', landingPageId)
      .single()

    if (!pageError) {
      const { error: updateError } = await supabase
        .from('landing_pages')
        .update({ submission_count: (pageData?.submission_count ?? 0) + 1 })
        .eq('id', landingPageId)

      if (updateError) {
        console.error('Failed to increment submission count', updateError)
      }
    }

    setLandingPages((prev) =>
      prev.map((page) =>
        page.id === landingPageId
          ? {
              ...page,
              submission_count: (page.submission_count ?? 0) + 1
            }
          : page
      )
    )

    return data
  }, [])

  const value = useMemo(
    () => ({
      landingPages,
      loading,
      error,
      fetchLandingPages,
      resetPageCache,
      createLandingPage,
      updateLandingPage,
      deleteLandingPage,
      toggleActiveState,
      checkSlugAvailability,
      checkingSlug,
      getLandingPageById,
      getPublicLandingPage,
      incrementViewCount,
      recordSubmission,
      uploadImage,
      DEFAULT_FORM_FIELDS,
      CTA_DEFAULTS
    }),
    [
      landingPages,
      loading,
      error,
      fetchLandingPages,
      resetPageCache,
      createLandingPage,
      updateLandingPage,
      deleteLandingPage,
      toggleActiveState,
      checkSlugAvailability,
      checkingSlug,
      getLandingPageById,
      getPublicLandingPage,
      incrementViewCount,
      recordSubmission,
      uploadImage
    ]
  )

  return <LandingPagesContext.Provider value={value}>{children}</LandingPagesContext.Provider>
}

export const useLandingPages = () => {
  const context = useContext(LandingPagesContext)
  if (!context) {
    throw new Error('useLandingPages must be used within a LandingPagesProvider')
  }
  return context
}

export const pageTypeLabels = {
  zoom_registration: 'Zoom Registration Page',
  recruiting: 'Recruiting Landing Page',
  client_focused: 'Client Focused Landing Page',
  hybrid: 'Hybrid Landing Page'
}

export default LandingPagesContext
